import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { track } from '@vercel/analytics';
import { usePDIContext } from '../context/PDIContext';
import { useUnlock } from '../hooks/useUnlock';
import { usePageMeta } from '../hooks/usePageMeta';
import { UNLOCK_PRICE_RUPEES } from '../utils/unlock';
import PrintableChecklist from '../components/PrintableChecklist';
import PaywallModal from '../components/PaywallModal';
import {
  Sun,
  Clock,
  FileCheck,
  PenLine,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  Sparkles,
  Shield,
  ArrowRight,
  Zap,
  Car,
  Camera,
  Printer,
  Download,
  ClipboardCheck,
  FileText,
  Scale,
  Lock,
} from 'lucide-react';

const GuidancePage = () => {
  const navigate = useNavigate();
  const { hasSeenGuidance, markGuidanceSeen, overallProgress, carProfile } = usePDIContext();
  const unlocked = useUnlock();
  const [showPrintableChecklist, setShowPrintableChecklist] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);

  usePageMeta({
    title: 'Free Pre-Delivery Inspection Checklist for New Cars in India',
    description: 'A free 100+ point PDI checklist for new car buyers in India - VIN decoder, odometer fraud checker, delivery blockers, and dealer negotiation scripts. Works offline.',
    path: '/',
  });

  const hasStarted = hasSeenGuidance && overallProgress.completed > 0;
  const hasProfile = Boolean(carProfile?.carMake || carProfile?.carModel || carProfile?.features?.length);

  const handleStartPDI = () => {
    markGuidanceSeen();
    track('started_inspection', { resumed: hasStarted });
    // First-timers set up their car first so feature checks are included
    navigate(hasStarted || hasProfile ? '/checklist' : '/car-profile');
  };

  const handlePrintable = () => {
    if (unlocked) {
      setShowPrintableChecklist(true);
    } else {
      setShowPaywall(true);
    }
  };

  const steps = [
    {
      icon: Car,
      title: 'Set up your car',
      description: 'Add your model & features - we tailor the checklist (sunroof, EV, ADAS...).',
      time: '2 min',
    },
    {
      icon: ClipboardCheck,
      title: 'Inspect, section by section',
      description: 'A guided flow walks you through 100+ checks. Add photos & notes as evidence.',
      time: '45-60 min',
    },
    {
      icon: FileText,
      title: 'Get your report',
      description: `A professional PDF report + dealer scripts to get issues fixed (₹${UNLOCK_PRICE_RUPEES}, one-time).`,
      time: 'Instant',
    },
  ];

  const guidelines = [
    {
      icon: Sun,
      title: 'Inspect in Daylight',
      description: 'Natural light reveals paint defects, scratches, and color inconsistencies.',
      gradient: 'from-amber-400 to-orange-500',
      bgLight: 'bg-gradient-to-br from-amber-50 to-orange-50',
    },
    {
      icon: Clock,
      title: 'Take Your Time',
      description: 'A thorough PDI takes 45-60 minutes. Don\'t let dealers rush you.',
      gradient: 'from-blue-400 to-indigo-500',
      bgLight: 'bg-gradient-to-br from-blue-50 to-indigo-50',
    },
    {
      icon: FileCheck,
      title: 'Match VIN Numbers',
      description: 'Verify VIN on dashboard, door frame, and documents all match.',
      gradient: 'from-emerald-400 to-teal-500',
      bgLight: 'bg-gradient-to-br from-emerald-50 to-teal-50',
    },
    {
      icon: PenLine,
      title: 'Sign Only When Complete',
      description: 'Never sign delivery docs until all issues are resolved.',
      gradient: 'from-rose-400 to-red-500',
      bgLight: 'bg-gradient-to-br from-rose-50 to-red-50',
    },
    {
      icon: Camera,
      title: 'Document Everything',
      description: 'Take photos of any issues found for your records.',
      gradient: 'from-violet-400 to-purple-500',
      bgLight: 'bg-gradient-to-br from-violet-50 to-purple-50',
    },
    {
      icon: Shield,
      title: 'Know Your Rights',
      description: 'You can reject delivery if critical issues aren\'t fixed.',
      gradient: 'from-cyan-400 to-teal-500',
      bgLight: 'bg-gradient-to-br from-cyan-50 to-teal-50',
    },
  ];

  const features = [
    { icon: CheckCircle2, text: 'Free 100+ item checklist' },
    { icon: Camera, text: 'Photo documentation' },
    { icon: FileCheck, text: 'PDF report' },
    { icon: Zap, text: 'Works offline' },
  ];

  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      {/* Hero Section */}
      <div className="relative hero-pattern px-4 pt-8 pb-10 -mt-4 -mx-4 mb-6 overflow-hidden">
        <div className="absolute top-10 right-10 w-32 h-32 bg-gradient-to-br from-brand-400/20 to-teal-400/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-40 h-40 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-3xl" />

        <div className="relative text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm border border-brand-100 mb-6 animate-bounce-in">
            <div className="w-6 h-6 rounded-full gradient-animated flex items-center justify-center">
              <Car className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-brand-700 text-sm font-semibold">Your New Car Awaits</span>
            <Sparkles className="w-4 h-4 text-amber-500" />
          </div>

          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">
            <span className="text-gradient">Pre-Delivery</span>
            <br />
            <span className="text-slate-800">Inspection</span>
          </h1>

          <p className="text-slate-600 text-lg max-w-md mx-auto leading-relaxed">
            Don't let excitement cloud your judgment. A thorough PDI ensures you get the
            <span className="font-semibold text-slate-700"> perfect car </span>
            you paid for.
          </p>

          <div className="flex flex-wrap justify-center gap-2 mt-6">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <div
                  key={i}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/70 backdrop-blur-sm rounded-full border border-slate-200/50 text-xs font-medium text-slate-600 shadow-sm"
                >
                  <Icon className="w-3.5 h-3.5 text-brand-500" />
                  {feature.text}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="px-4">
        {/* Continue card (if already started) */}
        {hasStarted && (
          <div
            className="relative overflow-hidden gradient-animated rounded-2xl p-6 mb-6 text-white shadow-xl cursor-pointer card-interactive glow-pulse"
            onClick={() => navigate('/checklist')}
          >
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full" />

            <div className="relative flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm font-medium flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Continue your inspection
                </p>
                <p className="text-4xl font-display font-bold mt-2">
                  {overallProgress.percentage}%
                </p>
                <p className="text-white/70 text-sm mt-1">
                  {overallProgress.completed} of {overallProgress.total} items checked
                </p>
              </div>
              <div className="w-20 h-20 relative">
                <svg className="w-20 h-20 -rotate-90" viewBox="0 0 36 36">
                  <path
                    className="text-white/20"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className="text-white"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeLinecap="round"
                    fill="none"
                    strokeDasharray={`${overallProgress.percentage}, 100`}
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
              </div>
            </div>
            <div className="relative flex items-center gap-2 mt-4 text-white/80">
              <span className="text-sm font-medium">Tap to continue</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>
        )}

        {/* How It Works */}
        <div className="mb-8">
          <h2 className="font-display text-xl font-bold text-slate-800 mb-4 flex items-center gap-3 pl-3 section-header">
            <span className="pl-3">How It Works</span>
          </h2>

          <div className="space-y-3">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-4 border-2 border-slate-100 shadow-sm flex items-start gap-4 animate-slide-up"
                  style={{ animationDelay: `${index * 60}ms` }}
                >
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 rounded-xl gradient-animated flex items-center justify-center shadow-lg">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center shadow">
                      {index + 1}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-bold text-slate-800">{step.title}</h3>
                      <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full flex-shrink-0">
                        {step.time}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1 leading-relaxed">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Start Button */}
        <button
          onClick={handleStartPDI}
          className="w-full relative overflow-hidden gradient-animated text-white font-bold py-5 px-6 rounded-2xl shadow-xl btn-touch flex items-center justify-center gap-3 text-lg group mb-8"
        >
          <span className="relative z-10">
            {hasStarted ? 'Continue Inspection' : 'Start Free Inspection'}
          </span>
          <ArrowRight className="relative z-10 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
        </button>

        {/* Before You Start Section */}
        <div className="mb-8">
          <h2 className="font-display text-xl font-bold text-slate-800 mb-4 flex items-center gap-3 pl-3 section-header">
            <span className="pl-3">Before You Start</span>
          </h2>

          <div className="grid gap-3 sm:grid-cols-2">
            {guidelines.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className={`${item.bgLight} rounded-2xl p-4 border border-white/50 shadow-sm animate-slide-up card-shine`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex gap-3">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{item.title}</h3>
                      <p className="text-sm text-slate-600 mt-0.5 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Critical Warning */}
        <div className="relative overflow-hidden bg-gradient-to-br from-danger-50 to-rose-50 border-2 border-danger-200 rounded-2xl p-5 mb-8">
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-danger-200/30 rounded-full blur-2xl" />
          <div className="relative flex gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-danger-500 to-rose-500 flex items-center justify-center flex-shrink-0 shadow-lg">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-danger-800 text-lg">Critical Reminder</h3>
              <p className="text-sm text-danger-700 mt-1 leading-relaxed">
                If any <strong>critical items fail</strong> (marked with a warning icon),
                <span className="text-danger-800 font-semibold"> do not accept delivery</span> until they are fixed.
                Always get written acknowledgment.
              </p>
              <Link
                to="/legal"
                className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-danger-700 hover:text-danger-800"
              >
                <Scale className="w-4 h-4" />
                Know your legal rights
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Printable Checklist Option */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-5 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-lg">
              <Printer className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-amber-900 text-lg">Prefer Pen & Paper?</h3>
                {!unlocked && (
                  <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-[10px] font-bold rounded-full">
                    ₹{UNLOCK_PRICE_RUPEES}
                  </span>
                )}
              </div>
              <p className="text-sm text-amber-700 mt-1 leading-relaxed mb-4">
                Download our printable checklist with all inspection items, tips, and checkboxes.
                Perfect for those who prefer an offline paper checklist at the dealership.
              </p>
              <button
                onClick={handlePrintable}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:from-amber-600 hover:to-orange-600 transition-all btn-touch"
              >
                {unlocked ? <Download className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                Download Printable Checklist
              </button>
            </div>
          </div>
        </div>

        {/* Helpful guides (static, SEO-indexed pages) */}
        <div className="mb-8">
          <h2 className="font-display text-xl font-bold text-slate-800 mb-4 flex items-center gap-3 pl-3 section-header">
            <span className="pl-3">Helpful Guides</span>
          </h2>
          <div className="grid gap-2 sm:grid-cols-2">
            {[
              { href: '/guide/pdi-checklist-new-cars-india/', label: 'The complete PDI checklist (104 points)' },
              { href: '/guide/can-you-refuse-car-delivery-india/', label: 'Can you refuse car delivery? Your rights' },
              { href: '/guide/check-car-manufacturing-date-vin/', label: 'Check the manufacturing date & spot old stock' },
              { href: '/guide/what-to-say-to-car-dealer-delivery/', label: 'What to say to the dealer at delivery' },
            ].map((g) => (
              <a
                key={g.href}
                href={g.href}
                className="flex items-center justify-between gap-2 p-3.5 bg-white border-2 border-slate-100 rounded-xl text-sm font-semibold text-slate-700 hover:border-brand-300 hover:text-brand-700 transition-colors"
              >
                {g.label}
                <ChevronRight className="w-4 h-4 flex-shrink-0 text-slate-400" />
              </a>
            ))}
          </div>
          <a href="/guide/" className="inline-block mt-3 text-sm font-semibold text-brand-600 hover:text-brand-700">
            Browse all guides →
          </a>
        </div>

        <p className="text-center text-xs text-slate-400 mt-4 mb-2 flex items-center justify-center gap-2">
          <Shield className="w-3.5 h-3.5" />
          No login • Data stored locally • Works offline
        </p>
      </div>

      {/* Printable Checklist Modal */}
      {showPrintableChecklist && (
        <PrintableChecklist onClose={() => setShowPrintableChecklist(false)} />
      )}

      {/* Paywall */}
      {showPaywall && (
        <PaywallModal
          feature="printable"
          onClose={() => setShowPaywall(false)}
          onUnlocked={() => {
            setShowPaywall(false);
            setShowPrintableChecklist(true);
          }}
        />
      )}
    </div>
  );
};

export default GuidancePage;
